import React, { useState, useRef, useEffect, useMemo, createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; 
import { 
  MessageSquare, BookHeart, Send, Compass, Sparkles, RefreshCw, Bot, User, 
  AlertCircle, BookmarkPlus, Map as MapIcon, ArrowRight, Calendar, StickyNote, 
  ArrowLeft, CheckCircle2, Circle, X, Save, Menu, Plane, HelpCircle, History, 
  MapPin, Cpu, Trash2, Share2, Clock, Plus, Users, Wallet, Globe, ChevronRight, 
  Layout, MousePointer2, Sun, Moon, AlertTriangle
} from 'lucide-react';

import img1 from "./rota_img/ankara_rota.jpg"
import img2 from "./rota_img/antalya_rota.webp"
import img3 from "./rota_img/bursa_rota.jpg"
import img4 from "./rota_img/istanbul_rota.webp"
import img5 from "./rota_img/izmir_rota.jpg"
import img6 from "./rota_img/konya_rota.jpg"
import img7 from "./rota_img/samsun_rota.webp"
import img8 from "./rota_img/sivas_rota.webp"
import img9 from "./rota_img/trabzon_rota.jpg"

// ==========================================
// 1. YAPILANDIRMA & TEMA CONTEXT
// ==========================================
const API_URL = process.env.REACT_APP_API_URL;

const SUGGESTION_PROMPTS = [
  "Hafta sonu İstanbul'da ne yapılır?",
  "Vizesiz gidilecek Balkan ülkeleri",
  "Kapadokya için 2 günlük rota",
  "İzmir'de tarihi yerler ve yemek rotası",
  "Karadeniz turu için en iyi mevsim",
  "Kamp yapılabilecek sakin yerler"
];

const ThemeContext = createContext();
const useTheme = () => useContext(ThemeContext);

// ==========================================
// 2. ORTAK STİLLER (MARKDOWN)
// ==========================================
const getMarkdownStyles = (isDarkMode) => ({
  h1: ({node, ...props}) => <h1 className={`text-3xl font-black mt-10 mb-6 pb-2 border-b flex items-center gap-3 tracking-tight ${isDarkMode ? 'text-white border-indigo-500/30' : 'text-slate-900 border-indigo-200'}`} {...props} />,
  h2: ({node, ...props}) => <h2 className={`text-2xl font-bold mt-8 mb-4 flex items-center gap-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`} {...props} />,
  h3: ({node, ...props}) => <h3 className={`text-xl font-bold mt-6 mb-3 ${isDarkMode ? 'text-indigo-200' : 'text-indigo-600'}`} {...props} />,
  p: ({node, ...props}) => <p className={`leading-relaxed mb-4 text-base md:text-lg ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} {...props} />,
  ul: ({node, ...props}) => <ul className="space-y-3 mb-6" {...props} />,
  li: ({node, children, ...props}) => (
    <li className="flex items-start gap-3 mb-2 group" {...props}>
      <span className={`mt-2.5 w-1.5 h-1.5 rounded-full shrink-0 block transition-colors ${isDarkMode ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]' : 'bg-indigo-600'}`}></span>
      <span className={`leading-relaxed flex-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{children}</span>
    </li>
  ),
  strong: ({node, ...props}) => <strong className={`font-bold px-1.5 py-0.5 rounded-md border mx-0.5 ${isDarkMode ? 'text-amber-400 bg-amber-900/20 border-amber-500/20' : 'text-amber-700 bg-amber-100 border-amber-200'}`} {...props} />,
  a: ({node, ...props}) => <a className={`underline underline-offset-4 decoration-indigo-500/30 hover:decoration-indigo-500 transition-all ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`} {...props} />,
  blockquote: ({node, ...props}) => <blockquote className={`border-l-4 pl-4 py-2 my-6 rounded-r-xl italic ${isDarkMode ? 'border-indigo-500/50 bg-slate-800/30 text-slate-400' : 'border-indigo-500/30 bg-indigo-50 text-slate-600'}`} {...props} />,
  table: ({node, ...props}) => <div className="overflow-x-auto mb-6 w-full"><table className={`min-w-full text-left text-sm border-collapse rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-white'}`} {...props} /></div>,
  thead: ({node, ...props}) => <thead className={isDarkMode ? 'bg-slate-800/80 text-slate-200' : 'bg-slate-100 text-slate-800'} {...props} />,
  th: ({node, ...props}) => <th className={`px-4 py-3 font-bold border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`} {...props} />,
  td: ({node, ...props}) => <td className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700/50 text-slate-300' : 'border-slate-200/50 text-slate-600'} align-top`} {...props} />,
  tbody: ({node, ...props}) => <tbody className={isDarkMode ? 'divide-y divide-slate-700/50' : 'divide-y divide-slate-200/50'} {...props} />,
});

// ==========================================
// 3. YARDIMCI FONKSİYONLAR & BACKEND
// ==========================================
const getStoredToken = () => {
  const possibleKeys = ['access_token', 'token', 'auth_token', 'jwt', 'user_id'];
  for (const key of possibleKeys) {
    const val = localStorage.getItem(key);
    if (val) return val;
  }
  return null;
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

const getUserInfoFromToken = () => {
  try {
    const token = getStoredToken();
    if (!token) return null;
    if (token.includes('.')) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        return {
          id: decoded.user_id || decoded.id || decoded.sub,
          name: decoded.name || decoded.username || "Gezgin"
        };
    }
    return { id: token, name: "Gezgin" };
  } catch (e) {
    return null;
  }
};

const BackendService = {
  syncUser: async (userData) => {
    try {
      const res = await fetch(`${API_URL}/ai/users`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(userData) });
      if (!res.ok) return userData; 
      return await res.json();
    } catch (e) { return userData; }
  },
  getChatHistory: async (userId) => {
    try {
      const res = await fetch(`${API_URL}/ai/chats/${userId}`, { headers: getAuthHeaders() });
      if (!res.ok) return [];
      const data = await res.json();
      return data.map(msg => ({ ...msg, timestamp: new Date(msg.timestamp) }));
    } catch (e) { return []; }
  },
  saveMessage: async (userId, message) => {
    try {
      await fetch(`${API_URL}/ai/chats/${userId}`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ id: message.id, role: message.role, text: message.text, is_error: message.isError || false })
      });
    } catch (e) { console.error("Mesaj kaydedilemedi:", e); }
    return message;
  },
  clearChat: async (userId) => {
    try { await fetch(`${API_URL}/ai/chats/${userId}`, { method: "DELETE", headers: getAuthHeaders() }); } 
    catch (e) { console.error("Sohbet temizlenemedi:", e); }
    return true;
  },
  getSavedRoutes: async (userId) => {
    try {
      const res = await fetch(`${API_URL}/ai/routes/${userId}`, { headers: getAuthHeaders() });
      if (!res.ok) return [];
      const data = await res.json();
      return data.map(r => ({ ...r, date: new Date(r.date) }));
    } catch (e) { return []; }
  },
  saveRoute: async (userId, routeData) => {
    try {
      const res = await fetch(`${API_URL}/ai/routes/${userId}`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ ...routeData, completedLines: routeData.completedLines || [] })
      });
      const savedRoute = await res.json();
      return { ...savedRoute, date: new Date(savedRoute.date) };
    } catch (e) { return routeData; }
  },
  deleteRoute: async (userId, routeId) => {
    try {
      const res = await fetch(`${API_URL}/ai/routes/${userId}/${routeId}`, { method: "DELETE", headers: getAuthHeaders() });
      return res.ok;
    } catch (e) { return false; }
  },
  updateRouteProgress: async (userId, routeId, completedLines) => {
    try {
      const res = await fetch(`${API_URL}/ai/routes/${routeId}/progress`, {
        method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ completedLines })
      });
      const updatedRoute = await res.json();
      return { ...updatedRoute, date: new Date(updatedRoute.date) };
    } catch (e) { return null; }
  },
  
  generateAIResponseSecure: async (history, userMessage, isRouteCreation = false) => {
    const response = await fetch(`${API_URL}/ai/generate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
            messages: history.filter(msg => !msg.isError && msg.id !== 'welcome').map(msg => ({ role: msg.role, text: msg.text })),
            user_message: userMessage,
            is_route_creation: isRouteCreation
        }),
    });

    if (response.status === 402) {
        throw new Error("Yetersiz bakiye! Lütfen cüzdanınıza token yükleyin.");
    }
    if (!response.ok) {
        throw new Error("Sunucu ile iletişim kurulamadı.");
    }

    const data = await response.json();
    return data.text;
  },

  // YENİ EKLENEN: Özellik Fiyatlarını Çekme
  getFeatureCosts: async () => {
    try {
      const res = await fetch(`${API_URL}/api/features/costs`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { 
      console.error("Fiyatlar çekilemedi:", e);
      return null; 
    }
  }
};

// ==========================================
// 5. YENİ BİLEŞENLER (NOTIFICATION & DELETE MODAL)
// ==========================================
const SuggestionBar = ({ onSelect }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className="flex gap-3 overflow-x-auto pb-6 px-1 scrollbar-hide mask-fade-right">
      {SUGGESTION_PROMPTS.map((prompt, index) => (
        <button key={index} onClick={() => onSelect(prompt)} className={`flex items-center gap-2 whitespace-nowrap backdrop-blur-md border px-4 py-3 rounded-2xl transition-all shadow-sm active:scale-95 group font-medium text-xs ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50 hover:border-indigo-500 hover:bg-slate-700 hover:text-indigo-400 text-slate-300' : 'bg-white/80 border-slate-200 hover:border-indigo-500 hover:bg-slate-50 hover:text-indigo-600 text-slate-600'}`}>
          <Sparkles size={14} className={isDarkMode ? "text-indigo-400 group-hover:text-indigo-300" : "text-indigo-500 group-hover:text-indigo-600"} /> {prompt}
        </button>
      ))}
    </div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
    const { isDarkMode } = useTheme();
    if (!isOpen) return null;
 
    return (
      <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200 ${isDarkMode ? 'bg-black/80' : 'bg-slate-500/40'}`}>
        <div className={`w-full max-w-sm rounded-[2rem] shadow-2xl p-6 border scale-100 animate-in zoom-in-95 duration-200 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex flex-col items-center text-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 ${isDarkMode ? 'bg-red-900/30 text-red-500' : 'bg-red-50 text-red-600'}`}>
              <AlertTriangle size={32} />
            </div>
            <div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Rotayı Sil?</h3>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Bu işlem geri alınamaz. Bu seyahat planını kütüphanenden kalıcı olarak silmek istediğine emin misin?
              </p>
            </div>
            <div className="flex gap-3 w-full mt-2">
              <button 
                onClick={onClose} 
                className={`flex-1 py-3 rounded-xl font-bold transition-colors ${isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Vazgeç
              </button>
              <button 
                onClick={onConfirm} 
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg shadow-red-500/30 transition-all active:scale-95"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      </div>
    );
};

const Notification = ({ message, type = 'success', isVisible, onClose }) => {
    const { isDarkMode } = useTheme();
    
    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }
    }, [isVisible, onClose]);

    if (!isVisible) return null;

    const isSuccess = type === 'success';

    return (
        <div className={`fixed top-6 right-6 z-[90] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border animate-in slide-in-from-right-10 duration-500 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isSuccess ? 'bg-emerald-500 text-white' : (type === 'warning' ? 'bg-amber-500 text-white' : 'bg-red-500 text-white')}`}>
                {isSuccess ? <CheckCircle2 size={18} /> : (type === 'warning' ? <Wallet size={18} /> : <AlertCircle size={18} />)}
            </div>
            <div>
                <h4 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{isSuccess ? 'Başarılı' : (type === 'warning' ? 'Uyarı' : 'Hata')}</h4>
                <p className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{message}</p>
            </div>
            <button onClick={onClose} className={`ml-2 p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-800 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}>
                <X size={16} />
            </button>
        </div>
    );
};

// ==========================================
// 🚨 YENİ: DİNAMİK MALİYET PROPLARI EKLENDİ 🚨
// ==========================================
const CreateTripModal = ({ isOpen, onClose, onSubmit, cost }) => {
  const { isDarkMode } = useTheme();
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState('3 gün');
  const [travelers, setTravelers] = useState('2 yolcu');
  const [budget, setBudget] = useState('Orta Seviye');

  const currentImage = useMemo(() => {
    const defaultImage = "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80";
    if (!destination) return defaultImage;
    
    const searchContent = destination.toLowerCase();
    if (searchContent.includes('ankara')) return img1;
    if (searchContent.includes('bursa')) return img3;
    if (searchContent.includes('istanbul')) return img4;
    if (searchContent.includes('antalya')) return img2;
    if (searchContent.includes('izmir')) return img5;
    if (searchContent.includes('konya')) return img6;
    if (searchContent.includes('trabzon')) return img9;
    if (searchContent.includes('sivas')) return img8;
    if (searchContent.includes('samsun')) return img7;
    
    return defaultImage;
  }, [destination]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!destination) return;
    const prompt = `${destination} için ${duration} sürecek, ${travelers} seçeneğiyle ve ${budget} bütçeli detaylı bir gezi rotası ve rehberi oluşturur musun? Lütfen sabah, öğle, akşam olarak planla.`;
    onSubmit(prompt, true); // true = isRouteCreation
  };

  const inputClass = `w-full p-4 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer font-medium border transition-all ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:border-transparent placeholder:text-slate-600' : 'bg-slate-50 border-slate-200 text-slate-800 focus:border-indigo-300 placeholder:text-slate-400'}`;
  const labelClass = `text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const InfoChip = ({ icon: Icon, text }) => (
    <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10">
        <Icon size={14} className="text-indigo-400" />
        <span className="text-sm font-medium text-white">{text}</span>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-300 ${isDarkMode ? 'bg-black/80' : 'bg-slate-500/40'}`}>
      <div className={`rounded-[2rem] w-full max-w-4xl h-auto max-h-[90vh] flex overflow-hidden shadow-2xl border ring-1 animate-in zoom-in-95 duration-300 ${isDarkMode ? 'bg-slate-900 border-slate-700/50 ring-white/10' : 'bg-white border-slate-200 ring-black/5'}`}>
        
        <div className="hidden md:block w-2/5 relative overflow-hidden group">
          <img src={currentImage} alt="Travel Destination" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
          <div className={`absolute inset-0 bg-gradient-to-t via-black/40 to-transparent opacity-90`}></div>
          <div className="absolute bottom-0 left-0 p-8 w-full z-10 flex flex-col gap-4">
            <div>
                 <p className="text-indigo-300 text-xs font-bold tracking-[0.2em] uppercase mb-2 animate-in slide-in-from-left-2">Yolculuk Hedefi</p>
                 <h3 className="text-4xl font-black text-white leading-tight break-words drop-shadow-lg animate-in slide-in-from-bottom-2">
                    {destination || "Hayalindeki Tatil"}
                 </h3>
            </div>
            <div className="flex flex-wrap gap-2 animate-in fade-in zoom-in duration-500 delay-100">
                <InfoChip icon={Clock} text={duration} />
                <InfoChip icon={Users} text={travelers} />
            </div>
             <div className="animate-in fade-in zoom-in duration-500 delay-200">
                <InfoChip icon={Wallet} text={budget} />
            </div>
            {!destination && (
                <p className="text-slate-300 text-sm italic mt-2 animate-pulse">
                    Detayları girdikçe burası güncellenecek...
                </p>
            )}
          </div>
        </div>

        <div className="flex-1 p-8 md:p-10 flex flex-col relative">
          <button onClick={onClose} className={`absolute top-6 right-6 p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'}`}>
            <X size={24} />
          </button>
          <h2 className={`text-2xl font-bold mb-8 flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
             <div className="bg-indigo-600 p-2 rounded-xl"><Compass size={24} className="text-white"/></div>
             Gezi Oluştur
          </h2>
          <div className="space-y-6 flex-1 overflow-y-auto custom-scrollbar pr-2">
            <div className="space-y-2">
              <label className={labelClass}><Globe size={14}/> Nereye gidiyorsun?</label>
              <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Örn: Roma, İtalya veya Kaş, Antalya" className={`${inputClass} cursor-text`} autoFocus/>
            </div>
            <div className="space-y-2">
              <label className={labelClass}><Clock size={14}/> Ne kadar sürecek?</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)} className={inputClass}>
                {["Günübirlik", "2 gün", "3 gün", "5 gün", "1 hafta", "10 gün", "2 hafta"].map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className={labelClass}><Users size={14}/> Kimle gidiyorsun?</label>
              <select value={travelers} onChange={(e) => setTravelers(e.target.value)} className={inputClass}>
                {["Tek başıma", "Çift (2 kişi)", "Aile (Çocuklu)", "Arkadaş Grubu", "İş Gezisi"].map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className={labelClass}><Wallet size={14}/> Bütçe Durumu</label>
              <select value={budget} onChange={(e) => setBudget(e.target.value)} className={inputClass}>
                {["Ekonomik (Öğrenci işi)", "Orta Seviye (Dengeli)", "Lüks (Konfor öncelikli)", "Bütçe Sınırsız"].map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>
          </div>
          <div className={`mt-8 pt-6 border-t flex justify-between items-center ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
             {/* 🚨 DİNAMİK MALİYET GÖSTERİMİ */}
             <div className="flex items-center gap-2 text-indigo-500 bg-indigo-500/10 px-3 py-1.5 rounded-lg border border-indigo-500/20">
                <Wallet size={16} />
                <span className="text-xs font-bold">Maliyet: {cost} Token</span>
             </div>
             
             <div className="flex gap-3">
                <button onClick={onClose} className={`px-6 py-3 font-bold rounded-xl transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100'}`}>Vazgeç</button>
                <button onClick={handleSubmit} disabled={!destination} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl shadow-lg shadow-indigo-900/40 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Sparkles size={18} /> Planı Oluştur
                </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, onSave }) => {
  const { isDarkMode } = useTheme();
  const isUser = message.role === 'user';
  const hasSaveAction = message.text.includes('[SAVE_ROUTE_ACTION]');
  const displayText = message.text.replace('[SAVE_ROUTE_ACTION]', '');
    
  const markdownStyles = useMemo(() => getMarkdownStyles(isDarkMode), [isDarkMode]);
     
  return (
    <div className={`flex w-full mb-8 ${isUser ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
      <div className={`flex max-w-[95%] md:max-w-[85%] gap-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center shadow-lg transition-transform hover:scale-105 ${isUser ? 'bg-gradient-to-tr from-indigo-600 to-violet-600 text-white shadow-indigo-500/20' : (isDarkMode ? 'bg-slate-800 border border-slate-700 text-indigo-400' : 'bg-white border border-slate-200 text-indigo-600')}`}>
          {isUser ? <User size={18} /> : message.isError ? <AlertCircle size={18} /> : <Cpu size={20} />}
        </div>
        <div className="flex flex-col items-start gap-1 w-full min-w-0">
          <div className={`px-6 py-5 rounded-3xl shadow-lg overflow-hidden text-sm md:text-base leading-relaxed w-full transition-all duration-300 ${isUser ? 'bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-tr-sm shadow-indigo-900/30' : (isDarkMode ? 'bg-slate-800/80 border border-slate-700/50 text-slate-100 rounded-tl-sm shadow-black/20' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-slate-200/50')}`}>
            {isUser ? <p className="whitespace-pre-wrap font-medium tracking-wide text-indigo-50">{displayText}</p> : (
              <div className="prose prose-invert prose-sm md:prose-base max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownStyles}>
                    {displayText}
                </ReactMarkdown>
              </div>
            )}
            {!isUser && hasSaveAction && onSave && (
              <div className={`mt-6 pt-5 border-t animate-in fade-in slide-in-from-bottom-2 ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                <button onClick={() => onSave('SAVE_TRIGGER')} className={`group w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl transition-all duration-300 font-semibold text-sm border hover:shadow-lg ${isDarkMode ? 'bg-slate-900 hover:bg-indigo-600 text-slate-300 hover:text-white border-slate-700 hover:border-indigo-500 hover:shadow-indigo-500/20' : 'bg-slate-50 hover:bg-indigo-600 text-slate-600 hover:text-white border-slate-200 hover:border-indigo-500 hover:shadow-indigo-500/20'}`}>
                  <BookmarkPlus size={18} className="transition-transform group-hover:scale-110" /> <span>Bu Rotayı Kütüphaneme Ekle</span>
                </button>
              </div>
            )}
          </div>
          <span className={`text-[10px] font-semibold px-2 mt-1.5 uppercase tracking-widest opacity-50 ${isUser ? (isDarkMode ? 'self-end text-indigo-300' : 'self-end text-indigo-600') : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}`}>{message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
        </div>
      </div>
    </div>
  );
};

const RouteDetail = ({ route, onBack, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const parsedContent = useMemo(() => route.content.split('\n'), [route.content]);
  const isListItem = (line) => /^(\s*[-*+]|\s*\d+\.)\s+/.test(line);
  const markdownStyles = useMemo(() => getMarkdownStyles(isDarkMode), [isDarkMode]);
    
  const toggleLine = async (index) => {
    const currentCompleted = route.completedLines || [];
    const newCompleted = currentCompleted.includes(index) ? currentCompleted.filter(i => i !== index) : [...currentCompleted, index];
    const optimisticRoute = { ...route, completedLines: newCompleted };
    onUpdate(optimisticRoute);
    const updatedRoute = await BackendService.updateRouteProgress(route.user_id, route.id, newCompleted);
    if (updatedRoute) onUpdate(updatedRoute);
  };
    
  const totalListItems = parsedContent.filter(line => isListItem(line)).length;
  const completedCount = (route.completedLines || []).length;
  const progressPercentage = totalListItems > 0 ? Math.round((completedCount / totalListItems) * 100) : 0;
    
  return (
    <div className={`flex flex-col lg:flex-row h-full animate-in fade-in slide-in-from-right-8 duration-500 relative overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className={`lg:w-[420px] shrink-0 border-r flex flex-col h-full relative z-20 shadow-2xl ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
         <div className="p-6 pb-2 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
            <button onClick={onBack} className={`flex items-center gap-2 py-2 px-4 rounded-full transition-all border font-bold text-xs group w-fit ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border-slate-200'}`}>
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> <span>Rotalara Dön</span>
            </button>
            <div className={`relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-2xl border group ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <img src={route.image} alt={route.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className={`absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-80 ${isDarkMode ? 'from-slate-900' : 'from-white'}`}></div>
                <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 text-[10px] text-white font-bold mb-2">
                        <MapPin size={12} className="text-indigo-400"/> {route.title.split(' ').slice(0,2).join(' ')}
                    </div>
                </div>
            </div>
            <div>
                <h1 className={`text-2xl md:text-3xl font-black leading-tight mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{route.title}</h1>
                <div className={`flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Calendar size={16} className="text-indigo-500"/>
                    <span>{new Date(route.date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
            </div>
            <div className={`p-5 rounded-2xl border relative overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex justify-between items-end mb-3 relative z-10">
                    <div>
                        <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gezi Durumu</p>
                        <p className={`text-2xl font-black flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>%{progressPercentage} <span className="text-sm font-medium text-slate-500 mt-1">tamamlandı</span></p>
                    </div>
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center border ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/20' : 'bg-indigo-100 text-indigo-600 border-indigo-200'}`}>
                        <Layout size={20} />
                    </div>
                </div>
                <div className={`w-full h-3 rounded-full overflow-hidden border relative z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700/50' : 'bg-slate-200 border-slate-300'}`}>
                    <div className="h-full bg-gradient-to-r from-indigo-600 to-violet-500 transition-all duration-700 ease-out rounded-full relative" style={{ width: `${progressPercentage}%` }}></div>
                </div>
            </div>
            {route.note && (
                <div className={`border p-5 rounded-2xl flex gap-4 items-start ${isDarkMode ? 'bg-amber-900/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                    <StickyNote size={20} className="text-amber-500 shrink-0 mt-1"/>
                    <div>
                        <p className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-1">Notun</p>
                        <p className={`text-sm leading-relaxed italic ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>"{route.note}"</p>
                    </div>
                </div>
            )}
         </div>
      </div>
      <div className={`flex-1 overflow-y-auto custom-scrollbar relative ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5 pointer-events-none"></div>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 pb-32 space-y-8 relative z-10">
          <div className={`mb-8 border-b pb-8 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
             <div className="flex items-center gap-3 text-indigo-500 mb-4">
                <span className="h-px w-8 bg-indigo-500/50"></span>
                <span className="text-xs font-bold uppercase tracking-[0.2em]">Seyahat Planın</span>
             </div>
             <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Bu rota senin için özel olarak hazırlandı. Adımları takip et, tamamladıkça işaretle ve anın tadını çıkar.
             </p>
          </div>
          <div className="space-y-4">
            {parsedContent.map((line, index) => {
              if (!line.trim()) return <div key={index} className="h-2"></div>;
              if (isListItem(line)) {
                const checked = route.completedLines?.includes(index);
                const cleanText = line.replace(/^(\s*[-*+]|\s*\d+\.)\s+/, '');
                return (
                  <div key={index} onClick={() => toggleLine(index)} className={`group cursor-pointer transition-all duration-300 ${checked ? 'opacity-50 grayscale' : 'hover:-translate-x-[-8px]'}`}>
                    <div className={`flex items-start gap-5 p-5 rounded-2xl border transition-all duration-300 ${checked ? (isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-100 border-slate-200') : (isDarkMode ? 'bg-slate-900/50 border-slate-800 hover:bg-slate-800 hover:border-indigo-500/30 hover:shadow-lg hover:shadow-indigo-500/5' : 'bg-white border-slate-200 hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-500/5')}`}>
                        <div className={`mt-1 shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 border ${checked ? 'bg-emerald-500 border-emerald-500 text-white' : (isDarkMode ? 'bg-transparent border-slate-600 text-transparent group-hover:border-indigo-400' : 'bg-transparent border-slate-300 text-transparent group-hover:border-indigo-500')}`}>
                            <CheckCircle2 size={14} className={`transition-all duration-300 ${checked ? 'scale-100' : 'scale-0'}`} />
                        </div>
                        <div className={`flex-1 text-base md:text-lg leading-relaxed ${checked ? (isDarkMode ? 'text-slate-500 line-through' : 'text-slate-400 line-through') : (isDarkMode ? 'text-slate-200' : 'text-slate-800')}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: 'span', strong: ({node, ...props}) => <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} {...props} /> }}>
                                {cleanText}
                            </ReactMarkdown>
                        </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={index} className="prose prose-lg max-w-none pl-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownStyles}>
                        {line}
                    </ReactMarkdown>
                </div>
              );
            })}
          </div>
          {completedCount === totalListItems && totalListItems > 0 && (
             <div className={`mt-12 p-8 rounded-3xl border text-center animate-in slide-in-from-bottom-4 ${isDarkMode ? 'bg-gradient-to-r from-emerald-900/40 to-slate-900 border-emerald-500/20' : 'bg-gradient-to-r from-emerald-50 to-white border-emerald-200 shadow-xl'}`}>
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30 text-white animate-bounce">
                    <Sparkles size={32} />
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-emerald-900'}`}>Tebrikler Gezgin!</h3>
                <p className={isDarkMode ? 'text-emerald-400/80' : 'text-emerald-600'}>Bu rotayı başarıyla tamamladın.</p>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SaveRouteModal = ({ isOpen, onClose, onSave, content }) => {
  const { isDarkMode } = useTheme();
  const [title, setTitle] = useState('Yeni Seyahat Rotam');
  const [note, setNote] = useState('');
  useEffect(() => { if (isOpen) { setTitle('Mekanname Gezi Planı'); setNote(''); } }, [isOpen]);
  if (!isOpen) return null;
  const cleanContent = content.replace(/\n\n💡 \*Bu planı saklamak istersen "Rota olarak kaydet" yazman yeterli\.\*[\s\S]*$/, '');
  const markdownStyles = getMarkdownStyles(isDarkMode);

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-lg animate-in fade-in duration-300 ${isDarkMode ? 'bg-black/70' : 'bg-slate-500/40'}`}>
      <div className={`rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col scale-100 animate-in zoom-in-95 duration-200 border ring-1 ${isDarkMode ? 'bg-slate-900 border-slate-700/50 ring-white/5' : 'bg-white border-slate-200 ring-black/5'}`}>
        <div className={`px-8 py-6 border-b flex justify-between items-center rounded-t-[2rem] ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-100 bg-white'}`}><div><h3 className={`font-black text-2xl flex items-center gap-3 ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}><div className="bg-indigo-600 p-2 rounded-xl text-white"><BookHeart size={24}/></div> Rotayı Kaydet</h3></div><button onClick={onClose} className={`p-2.5 rounded-full transition-colors border ${isDarkMode ? 'bg-slate-800 text-slate-400 hover:text-red-400 hover:bg-red-900/20 border-slate-700' : 'bg-slate-50 text-slate-500 hover:text-red-600 hover:bg-red-50 border-slate-200'}`}><X size={20} /></button></div>
        <div className={`p-8 overflow-y-auto flex-1 space-y-6 custom-scrollbar ${isDarkMode ? 'bg-slate-950/50' : 'bg-slate-50'}`}>
          <div className="space-y-5">
            <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ml-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Rota Başlığı</label><div className="relative group"><input value={title} onChange={(e) => setTitle(e.target.value)} className={`w-full pl-5 pr-4 py-4 border rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all font-bold text-lg shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-200 group-hover:border-slate-600 placeholder:text-slate-600' : 'bg-white border-slate-200 text-slate-900 group-hover:border-indigo-300 placeholder:text-slate-400'}`} placeholder="Örn: Kapadokya Gezisi" autoFocus /></div></div>
            <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ml-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Kişisel Not</label><div className="relative group"><StickyNote size={18} className={`absolute top-4 left-5 transition-colors ${isDarkMode ? 'text-slate-600 group-focus-within:text-indigo-400' : 'text-slate-400 group-focus-within:text-indigo-500'}`}/><input value={note} onChange={(e) => setNote(e.target.value)} className={`w-full pl-12 pr-4 py-4 border rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all shadow-sm font-medium ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300 group-hover:border-slate-600 placeholder:text-slate-600' : 'bg-white border-slate-200 text-slate-700 group-hover:border-indigo-300 placeholder:text-slate-400'}`} placeholder="Unutulmayacaklar, bütçe vs..." /></div></div>
          </div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ml-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Plan Özeti</label><div className={`rounded-2xl p-6 border h-40 overflow-y-auto text-sm shadow-inner leading-relaxed scrollbar-thin scrollbar-thumb-slate-700 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-800 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}><div className="prose prose-sm prose-invert max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownStyles}>{cleanContent}</ReactMarkdown></div></div></div>
        </div>
        <div className={`px-8 py-6 border-t rounded-b-[2rem] flex justify-end gap-3 ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-100 bg-white'}`}><button onClick={onClose} className={`px-6 py-3 font-bold rounded-xl transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>Vazgeç</button><button onClick={() => onSave(title, note)} disabled={!title.trim()} className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 shadow-xl shadow-indigo-900/40 flex items-center gap-2 font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 transform"><Save size={18} /> Kaydet</button></div>
      </div>
    </div>
  );
};

// 🚨 YENİ: DİNAMİK MALİYET PROPLARI EKLENDİ 🚨
const HelpModal = ({ isOpen, onClose, costs }) => {
  const { isDarkMode } = useTheme();
  if (!isOpen) return null;
  const FeatureStep = ({ icon: Icon, title, desc, colorClass }) => (
    <div className={`flex gap-5 p-6 rounded-3xl border transition-all group cursor-default ${isDarkMode ? 'bg-slate-900 border-slate-800 hover:border-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/5' : 'bg-white border-slate-200 hover:border-indigo-400 hover:shadow-xl hover:shadow-indigo-500/10'}`}>
      <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ${colorClass} text-white shadow-lg transform group-hover:rotate-6 transition-transform duration-300`}><Icon size={26} /></div>
      <div><h4 className={`font-bold mb-2 text-lg transition-colors ${isDarkMode ? 'text-slate-200 group-hover:text-indigo-400' : 'text-slate-800 group-hover:text-indigo-600'}`}>{title}</h4><p className={`text-sm leading-relaxed font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{desc}</p></div>
    </div>
  );
  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-xl animate-in fade-in duration-300 ${isDarkMode ? 'bg-black/80' : 'bg-slate-500/40'}`}>
      <div className={`rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col md:flex-row overflow-hidden animate-in zoom-in-95 duration-300 relative border ${isDarkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-transparent'}`}>
        <button onClick={onClose} className={`absolute top-6 right-6 p-2.5 backdrop-blur-md rounded-full transition-colors z-30 ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white' : 'bg-black/5 hover:bg-black/10 text-slate-600 hover:text-black'}`}><X size={20} /></button>
        <div className="bg-black text-white p-12 md:w-2/5 flex flex-col justify-center relative overflow-hidden"><div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-900/40 via-black to-black z-0"></div><div className="relative z-10"><div className="w-24 h-24 bg-gradient-to-br from-indigo-600 to-violet-900 rounded-3xl flex items-center justify-center mb-10 shadow-2xl shadow-indigo-500/20 ring-1 ring-white/10"><Cpu size={40} className="text-white animate-pulse" /></div><h2 className="text-5xl font-black mb-6 leading-tight tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">Mekânname<br/>AI</h2><p className="text-slate-400 text-lg leading-relaxed font-medium">Yeni nesil seyahat asistanın ile tanış. Rota planlamayı saniyeler süren keyifli bir sohbete dönüştürüyoruz.</p></div></div>
        <div className={`p-10 md:p-14 md:w-3/5 overflow-y-auto custom-scrollbar ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
          <div className="grid grid-cols-1 gap-4">
              <FeatureStep icon={MessageSquare} colorClass="bg-indigo-600" title={`1. Asistana Danış (${costs.ai_chat_prompt} Token)`} desc="Gitmek istediğin şehri ve ilgi alanlarını yaz. Örn: 'Hafta sonu Kapadokya'da ne yapılır?'" />
              <FeatureStep icon={Bot} colorClass="bg-violet-600" title={`2. Rota Oluştur (${costs.create_trip} Token)`} desc="Yapay zeka sana özel, saatlik veya günlük planlar hazırlasın. Beğenmediğin yerleri revize et." />
              <FeatureStep icon={BookmarkPlus} colorClass="bg-fuchsia-600" title="3. Rotayı Kaydet (Ücretsiz)" desc="Planı beğendiysen 'Rotayı Kaydet' butonuna tıkla. Kütüphanene anında eklensin." />
          </div>
          <div className="mt-10 flex justify-end"><button onClick={onClose} className="px-10 py-4 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-900/20 hover:shadow-indigo-600/40 active:scale-95 text-lg">Keşfetmeye Başla</button></div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 6. ANA UYGULAMA (GÜNCELLENDİ)
// ==========================================
const MekannameApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeView, setActiveView] = useState('chat');
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateTripOpen, setIsCreateTripOpen] = useState(false); 
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [pendingContent, setPendingContent] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  const [deleteModalState, setDeleteModalState] = useState({ isOpen: false, routeId: null });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // 🚨 YENİ: DİNAMİK MALİYETLER İÇİN STATE
  const [tokenCosts, setTokenCosts] = useState({ ai_chat_prompt: 50, create_trip: 100 });
  
  const messagesEndRef = useRef(null);

  const themeValue = useMemo(() => ({ isDarkMode, setIsDarkMode }), [isDarkMode]);

  useEffect(() => {
    const initApp = async () => {
      setIsDataLoading(true);
      try {
        // Maliyetleri Çek
        const costs = await BackendService.getFeatureCosts();
        if (costs) {
            setTokenCosts({
                ai_chat_prompt: costs.ai_chat_prompt || 50,
                create_trip: costs.create_trip || 100
            });
        }

        const userInfo = getUserInfoFromToken();
        if (userInfo && userInfo.id) {
          const user = await BackendService.syncUser({ id: userInfo.id, name: userInfo.name, plan: "Pro" });
          if (user) {
            setCurrentUser(user);
            const [chatData, routesData] = await Promise.all([
              BackendService.getChatHistory(user.id),
              BackendService.getSavedRoutes(user.id)
            ]);
            if (chatData.length > 0) setMessages(chatData);
            else setMessages([{ id: 'welcome', role: 'model', text: `Merhaba! Ben Mekânname AI. Nereyi keşfetmek istersin? Sana özel bir gezi rotası oluşturabilirim.`, timestamp: new Date() }]);
            setSavedRoutes(routesData);
          }
        }
      } catch (error) { console.error(error); } finally { setIsDataLoading(false); }
    };
    initApp();
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isLoading]);

  const showNotification = (message, type = 'success') => {
      setNotification({ show: true, message, type });
  };

  const handleSend = async (overrideInput = null, isRouteCreation = false) => {
    const textToSend = overrideInput || input;
    if (!textToSend.trim() || isLoading) return;
    
    const userMsg = { id: Date.now().toString(), role: 'user', text: textToSend.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    
    if (currentUser) BackendService.saveMessage(currentUser.id, userMsg);
    
    try {
      const responseText = await BackendService.generateAIResponseSecure(messages, userMsg.text, isRouteCreation);
      
      const aiMsg = { id: (Date.now() + 1).toString(), role: 'model', text: responseText, timestamp: new Date() };
      setMessages(prev => [...prev, aiMsg]);
      if (currentUser) BackendService.saveMessage(currentUser.id, aiMsg);
      
    } catch (error) {
      if (error.message.includes("Yetersiz bakiye")) {
          showNotification('Yetersiz Token! Lütfen cüzdanınıza yükleme yapın.', 'warning');
      }
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'model', text: `Hata: ${error.message || "Bir sorun oluştu."}`, timestamp: new Date(), isError: true }]);
    } finally { setIsLoading(false); }
  };

  const handleCreateTripSubmit = (prompt, isRouteCreation) => {
    setIsCreateTripOpen(false);
    setActiveView('chat');
    handleSend(prompt, isRouteCreation);
  };

  const handleSuggestionClick = (prompt) => { setInput(prompt); };
  const handleClearChat = async () => { setMessages([{ id: Date.now().toString(), role: 'model', text: 'Sohbet ekranı temizlendi! Geçmiş konuşmalarına "Sohbet Geçmişi" menüsünden ulaşabilirsin.', timestamp: new Date() }]); };
  const handleSaveTrigger = (trigger) => { if (trigger === 'SAVE_TRIGGER') { const routeMsg = [...messages].reverse().find(m => m.role === 'model' && m.text.length > 100 && !m.text.includes('[SAVE_ROUTE_ACTION]')); if (routeMsg) { setPendingContent(routeMsg.text); setIsModalOpen(true); } else { alert("Kaydedilecek uygun bir rota bulunamadı."); } } };
  
  const handleDeleteClick = (e, routeId) => {
    e.stopPropagation(); 
    setDeleteModalState({ isOpen: true, routeId });
  };

  const confirmDeleteRoute = async () => {
    const routeId = deleteModalState.routeId;
    if (!routeId) return;

    setDeleteModalState({ isOpen: false, routeId: null });

    const previousRoutes = [...savedRoutes];
    setSavedRoutes(prev => prev.filter(r => r.id !== routeId));

    if (currentUser) {
        const success = await BackendService.deleteRoute(currentUser.id, routeId);
        if (success) {
            showNotification('Rota başarıyla silindi', 'success');
        } else {
            setSavedRoutes(previousRoutes);
            showNotification('Silme işlemi başarısız oldu', 'error');
        }
    }
  };

  const confirmSave = async (title, note) => {
    const defaultImages = [
      "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=800&q=80", 
      "https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?w=800&q=80", 
      "https://images.unsplash.com/photo-1563294628-985223c6f6ba?w=800&q=80", 
      "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80", 
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80"
    ];

    const searchContent = (title + " " + pendingContent).toLowerCase();
    let selectedImage = defaultImages[Math.floor(Math.random() * defaultImages.length)];

    if (searchContent.includes('ankara')) selectedImage = img1;
    else if (searchContent.includes('bursa')) selectedImage = img3;
    else if (searchContent.includes('istanbul')) selectedImage = img4;
    else if (searchContent.includes('antalya')) selectedImage = img2;
    else if (searchContent.includes('izmir')) selectedImage = img5;
    else if (searchContent.includes('konya')) selectedImage = img6;
    else if (searchContent.includes('trabzon')) selectedImage = img9;
    else if (searchContent.includes('sivas')) selectedImage = img8;
    else if (searchContent.includes('samsun')) selectedImage = img7;

    const cleanContent = pendingContent.replace(/\n\n💡 \*Bu planı saklamak istersen "Rota olarak kaydet" yazman yeterli\.\*[\s\S]*$/, '');
    
    const routeData = { 
        id: Date.now().toString(), 
        title, content: cleanContent, note, date: new Date(), image: selectedImage, completedLines: [] 
    };

    if (currentUser) { 
        const savedRoute = await BackendService.saveRoute(currentUser.id, routeData); 
        setSavedRoutes(prev => [savedRoute, ...prev]); 
        showNotification('Rota kütüphanene eklendi!', 'success');
    }
    
    setIsModalOpen(false); setActiveView('saved'); setSelectedRouteId(null);
  };

  if (isDataLoading) return <div className={`h-screen w-full flex items-center justify-center flex-col gap-8 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-800'}`}><div className="relative"><div className="absolute inset-0 bg-indigo-500 blur-2xl opacity-40 animate-pulse"></div><div className={`relative z-10 p-6 rounded-3xl border shadow-2xl ${isDarkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}><Plane size={48} className="text-indigo-500 animate-bounce" /></div></div><div className="flex flex-col items-center gap-2"><h2 className="text-3xl font-black tracking-tight">Mekânname</h2><div className="flex items-center gap-2 text-indigo-500 font-medium text-sm"><span className="w-2 h-2 bg-indigo-500 rounded-full animate-ping"></span>Sistem başlatılıyor...</div></div></div>;

  const NavButton = ({ id, icon: Icon, label, badge, action }) => {
    const isActive = activeView === id;
    return (
        <button onClick={action || (() => { setActiveView(id); setSelectedRouteId(null); setIsSidebarOpen(false); })} className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${isActive ? 'bg-indigo-600 shadow-lg shadow-indigo-500/40 text-white' : (isDarkMode ? 'hover:bg-white/5 text-slate-400 hover:text-white' : 'hover:bg-indigo-50 text-slate-500 hover:text-indigo-700')}`}>
            <div className={`absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isActive ? 'opacity-100' : ''}`}></div>
            <Icon size={20} className={`relative z-10 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} /> 
            <span className={`relative z-10 font-medium tracking-wide flex-1 text-left text-sm ${isActive ? 'font-bold' : ''}`}>{label}</span>
            {badge > 0 && <span className={`relative z-10 text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm ${isActive ? 'bg-white text-indigo-600' : 'bg-indigo-100 text-indigo-600'}`}>{badge}</span>}
        </button>
    );
  };

  return (
    <ThemeContext.Provider value={themeValue}>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(99, 102, 241, 0.2); border-radius: 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(99, 102, 241, 0.5); }
        .mask-fade-right { -webkit-mask-image: linear-gradient(to right, black 90%, transparent 100%); mask-image: linear-gradient(to right, black 90%, transparent 100%); }
        ::selection { background-color: rgba(99, 102, 241, 0.3); color: white; }
      `}</style>

      <div className={`flex h-screen w-full overflow-hidden font-sans transition-colors duration-500 ${isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-800'}`}>
        
        <aside className={`hidden md:flex flex-col w-72 h-[calc(100vh-2rem)] m-4 p-6 shrink-0 z-50 shadow-2xl relative overflow-hidden rounded-[2rem] border transition-colors duration-500 ${isDarkMode ? 'bg-slate-950 border-slate-800/50 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <a href="/">
          <div className="mb-6 flex items-center gap-4 relative z-10">
              <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-3 rounded-2xl text-white shadow-xl shadow-indigo-500/20 ring-1 ring-white/10"><MapIcon size={24} strokeWidth={2.5} /></div>
              <div><h2 className={`text-xl font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Mekânname</h2><p className="text-[10px] text-indigo-500 font-bold tracking-[0.2em] uppercase mt-0.5">AI Travel Assistant</p></div>
          </div>
            </a>

          <div className="relative z-10 mb-6">
            <button onClick={() => setIsCreateTripOpen(true)} className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-lg shadow-indigo-500/30 transition-all hover:scale-[1.02] active:scale-95 group">
                <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                <span>Yeni Rota Oluştur</span>
            </button>
          </div>

          <div className="space-y-2 flex-1 relative z-10"><NavButton id="chat" icon={MessageSquare} label="Gezi Asistanı" /><NavButton id="saved" icon={BookHeart} label="Rotalarım" badge={savedRoutes.length} /><NavButton id="history" icon={History} label="Sohbet Geçmişi" action={async () => { setActiveView('history'); setSelectedRouteId(null); if(currentUser) { const history = await BackendService.getChatHistory(currentUser.id); setHistoryMessages(history); } }}/></div>
          
          <div className="relative z-10 space-y-4">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isDarkMode ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-indigo-600'}`}>
                <div className="flex items-center gap-3"><span className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-500">{isDarkMode ? <Moon size={16}/> : <Sun size={16}/>}</span> <span className="text-xs font-bold uppercase tracking-wide">Görünüm</span></div>
                <div className={`w-10 h-5 rounded-full p-0.5 transition-colors ${isDarkMode ? 'bg-indigo-600' : 'bg-slate-300'}`}><div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}`}></div></div>
            </button>

            <NavButton id="help" icon={HelpCircle} label="Nasıl Çalışır?" action={() => setIsHelpOpen(true)} />
            
            <div className={`pt-4 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
                <div className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-colors cursor-pointer group backdrop-blur-sm ${isDarkMode ? 'bg-white/5 border-white/5 hover:border-white/10' : 'bg-slate-50 border-slate-100 hover:border-indigo-100'}`}>
                    <div className="bg-gradient-to-tr from-indigo-500 to-violet-600 h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white/20">{currentUser?.name?.charAt(0) || 'M'}</div>
                    <div className="flex-1 min-w-0"><p className={`text-sm font-bold truncate transition-colors ${isDarkMode ? 'text-white group-hover:text-indigo-300' : 'text-slate-800 group-hover:text-indigo-600'}`}>{currentUser?.name || 'Misafir'}</p><p className="text-[11px] text-slate-500 truncate font-medium uppercase tracking-wide">{currentUser?.plan || 'Free'} Plan</p></div>
                </div>
            </div>
          </div>
        </aside>

        <div className={`flex-1 flex flex-col h-full relative overflow-hidden transition-colors duration-500 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
          
          <div className={`md:hidden p-4 flex justify-between items-center shrink-0 z-40 shadow-xl border-b ${isDarkMode ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-900 border-slate-200'}`}>
              <div className="flex items-center gap-3"><div className="bg-indigo-600 p-2 rounded-xl text-white"><MapIcon size={20} /></div><span className="font-bold text-lg tracking-tight">Mekânname</span></div>
              <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={`p-2 rounded-lg border transition-colors ${isDarkMode ? 'text-slate-300 hover:text-white bg-white/5 border-white/5' : 'text-slate-600 hover:text-indigo-600 bg-slate-100 border-slate-200'}`}>{isSidebarOpen ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
          {isSidebarOpen && (<div className="md:hidden absolute inset-0 z-30 bg-black/95 backdrop-blur-xl p-6 flex flex-col animate-in fade-in slide-in-from-top-5"><div className="space-y-3 mt-24"><button onClick={() => {setIsCreateTripOpen(true); setIsSidebarOpen(false);}} className="w-full flex items-center justify-center gap-2 p-4 mb-6 rounded-2xl bg-indigo-600 text-white font-bold"><Plus size={20}/> Yeni Rota Oluştur</button><NavButton id="chat" icon={MessageSquare} label="Gezi Asistanı" /><NavButton id="saved" icon={BookHeart} label="Rotalarım" /><NavButton id="history" icon={History} label="Geçmiş Sohbetler" action={async () => { setActiveView('history'); setSelectedRouteId(null); setIsSidebarOpen(false); if(currentUser) { const history = await BackendService.getChatHistory(currentUser.id); setHistoryMessages(history); } }}/><NavButton id="help" icon={HelpCircle} label="Nasıl Çalışır?" action={() => { setIsHelpOpen(true); setIsSidebarOpen(false); }} /></div></div>)}

          <div className={`flex-1 flex flex-col min-h-0 relative ${activeView === 'chat' ? 'flex' : 'hidden'}`}>
             <header className={`backdrop-blur-md border-b px-6 py-4 flex justify-between items-center z-10 sticky top-0 shadow-sm transition-colors duration-500 ${isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white/80 border-slate-200'}`}>
                 <div className="flex items-center gap-4">
                     <div className={`p-3 rounded-2xl border shadow-sm hidden md:block ${isDarkMode ? 'bg-indigo-900/30 text-indigo-400 border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border-indigo-100'}`}><Compass size={22} /></div>
                     <div><h1 className={`font-bold text-lg tracking-tight ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>Mekânname Asistan</h1><div className="flex items-center gap-1.5 mt-0.5"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span><p className="text-xs text-slate-400 font-medium hidden md:block">AI Aktif</p></div></div>
                 </div>
                 <button onClick={handleClearChat} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all border active:scale-95 ${isDarkMode ? 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800 bg-slate-900 hover:shadow-md border-slate-800' : 'text-slate-500 hover:text-indigo-600 hover:bg-slate-50 bg-white hover:shadow-md border-slate-200'}`}><RefreshCw size={16}/> <span className="hidden sm:inline">Temizle</span></button>
             </header>
             
             <div className={`flex-1 overflow-y-auto p-4 md:p-8 space-y-2 scroll-smooth custom-scrollbar ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                 <div className="max-w-4xl mx-auto pb-32">
                      {messages.map(msg => <MessageBubble key={msg.id} message={msg} onSave={handleSaveTrigger} />)}
                      {isLoading && (<div className={`flex items-center gap-3 text-sm animate-in fade-in zoom-in slide-in-from-bottom-2 px-6 py-4 rounded-[2rem] w-fit shadow-lg border ${isDarkMode ? 'bg-slate-800 text-indigo-400 shadow-black/40 border-slate-700' : 'bg-white text-indigo-600 shadow-slate-200 border-slate-100'}`}><div className="flex gap-1"><span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span><span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span><span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"></span></div><span className="font-semibold tracking-wide ml-2">Mekânname düşünüyor...</span></div>)}
                      <div ref={messagesEndRef} className="h-6" />
                 </div>
             </div>
             <div className="absolute bottom-6 left-0 right-0 px-4 md:px-8 z-20 pointer-events-none">
                 <div className="max-w-4xl mx-auto pointer-events-auto">
                     {!isLoading && messages.length < 3 && (<SuggestionBar onSelect={handleSuggestionClick} />)}
                     <div className={`relative flex gap-3 items-end backdrop-blur-xl rounded-[2rem] shadow-2xl border transition-all duration-300 focus-within:ring-2 focus-within:ring-indigo-500/50 focus-within:shadow-indigo-500/20 ${isDarkMode ? 'bg-slate-800/90 shadow-black/50 border-slate-700/50' : 'bg-white/90 shadow-slate-300/50 border-slate-200'}`}>
                         <textarea 
                             value={input} 
                             onChange={(e) => setInput(e.target.value)} 
                             onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}} 
                             // 🚨 DİNAMİK MALİYET BURAYA EKLENDİ 🚨
                             placeholder={`Sohbet et (Maliyet: ${tokenCosts.ai_chat_prompt} Token)...`} 
                             className={`w-full bg-transparent border-none focus:ring-0 px-6 py-4 min-h-[50px] max-h-[120px] text-lg resize-none font-medium ${isDarkMode ? 'text-slate-200 placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400'}`} 
                             rows={1} 
                         />
                         <button 
                             onClick={() => handleSend(null, false)} 
                             disabled={!input.trim() || isLoading} 
                             className={`h-[56px] w-[56px] flex items-center justify-center rounded-full transition-all active:scale-95 shadow-lg shrink-0 mb-0.5 mr-0.5 ${!input.trim() || isLoading ? (isDarkMode ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed') : 'bg-indigo-600 text-white hover:bg-indigo-500'}`}
                         >
                             <Send size={22} className={input.trim() ? 'ml-1' : ''}/>
                         </button>
                     </div>
                     <p className="text-center text-[10px] text-slate-500 mt-3 font-medium opacity-60">Mekânname AI hata yapabilir • v1.1</p>
                 </div>
             </div>
          </div>
          
          {activeView === 'saved' && (
              selectedRouteId 
              ? (<RouteDetail route={savedRoutes.find(r => r.id === selectedRouteId)} onBack={() => setSelectedRouteId(null)} onUpdate={(updated) => setSavedRoutes(prev => prev.map(r => r.id === updated.id ? updated : r))} />) 
              : (
              <div className="flex-1 flex flex-col min-h-0">
                  <header className={`backdrop-blur-md border-b px-6 md:px-12 py-6 sticky top-0 z-10 transition-colors ${isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-200'}`}>
                      <div className="max-w-7xl mx-auto flex items-center gap-4">
                          <div className="bg-indigo-600 p-2.5 rounded-2xl text-white shadow-lg shadow-indigo-500/30"><BookHeart size={32}/></div>
                          <div>
                              <h2 className={`text-3xl font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Rotalarım</h2>
                              <p className="text-slate-400 text-sm font-medium">Kaydettiğin tüm seyahat planları ve anıların.</p>
                          </div>
                      </div>
                  </header>

                  <div className="flex-1 overflow-y-auto p-6 md:p-12 custom-scrollbar">
                      <div className="max-w-7xl mx-auto">
                          {savedRoutes.length === 0 ? (<div className={`flex flex-col items-center justify-center py-32 border-2 border-dashed rounded-[2.5rem] ${isDarkMode ? 'border-slate-800 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}><div className={`p-8 rounded-full shadow-inner mb-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}><MapIcon size={64} className="text-slate-400" /></div><h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Henüz kayıtlı rota yok</h3><p className="text-slate-500 max-w-sm text-center mt-2 mb-8">Asistan ile konuşarak harika bir rota oluştur ve "Kaydet" butonuna bas.</p><button onClick={() => {setActiveView('chat'); setIsCreateTripOpen(true);}} className="px-8 py-4 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-500 transition-all shadow-xl hover:shadow-indigo-600/30 font-bold">Hemen Rota Oluştur</button></div>) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-10">
                                  {savedRoutes.map(route => (
                                      <div key={route.id} onClick={() => setSelectedRouteId(route.id)} className={`rounded-[2rem] shadow-lg border overflow-hidden group hover:shadow-2xl transition-all duration-500 cursor-pointer flex flex-col h-full relative ${isDarkMode ? 'bg-slate-800 border-slate-700/50 hover:shadow-indigo-500/10' : 'bg-white border-slate-200 hover:shadow-indigo-500/10'}`}>
                                          <div className="h-64 relative overflow-hidden">
                                              <img src={route.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 opacity-90 group-hover:opacity-100" alt=""/>
                                              <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-slate-900 via-slate-900/40' : 'from-white via-white/10'} to-transparent`}></div>
                                              
                                              <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-md px-4 py-2 rounded-xl text-white text-sm font-black border border-white/20 shadow-xl z-20">
                                                {(route.completedLines?.length || 0)} / {(route.content.match(/^(\s*[-*+]|\s*\d+\.)\s+/gm) || []).length} Adım
                                              </div>
                                              
                                              <button 
                                                onClick={(e) => handleDeleteClick(e, route.id)}
                                                className="absolute top-4 right-4 z-30 p-2.5 bg-red-500/80 hover:bg-red-600 backdrop-blur-md text-white rounded-xl transition-all shadow-lg hover:shadow-red-500/30 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0"
                                                title="Rotayı Sil"
                                              >
                                                <Trash2 size={16} />
                                              </button>

                                              <div className={`absolute bottom-0 left-0 p-8 w-full ${isDarkMode ? 'text-white' : 'text-slate-900'}`}><h3 className="font-bold text-2xl leading-snug mb-2 shadow-sm drop-shadow-md transition-colors line-clamp-2">{route.title}</h3><div className="text-xs text-slate-500 flex gap-2 items-center font-bold tracking-wider uppercase opacity-80"><Calendar size={14}/> {new Date(route.date).toLocaleDateString('tr-TR')}</div></div>
                                          </div>
                                          <div className="p-8 flex-1 flex flex-col">
                                              {route.note ? (<div className={`text-xs px-4 py-3 rounded-xl mb-5 italic border flex gap-3 ${isDarkMode ? 'bg-amber-900/20 text-amber-500 border-amber-900/30' : 'bg-amber-50 text-amber-700 border-amber-100'}`}><StickyNote size={14} className="shrink-0 mt-0.5 text-amber-500"/> <span className="line-clamp-2 font-medium">{route.note}</span></div>) : <div className="mb-4"></div>}
                                              <div className={`relative h-28 overflow-hidden text-sm mb-6 transition-colors ${isDarkMode ? 'text-slate-300 group-hover:text-slate-200' : 'text-slate-600 group-hover:text-slate-800'}`}>
                                                  <div className="line-clamp-4 prose prose-sm leading-relaxed prose-slate">
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: 'p', strong: 'strong' }}>{route.content}</ReactMarkdown>
                                                  </div>
                                                  <div className={`absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t ${isDarkMode ? 'from-slate-800 via-slate-800/90' : 'from-white via-white/90'} to-transparent pointer-events-none`}></div>
                                              </div>
                                              <div className={`flex justify-between items-center pt-5 border-t mt-auto group/btn ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}><span className="text-sm font-bold text-indigo-500">Detayları İncele</span><div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm border ${isDarkMode ? 'bg-slate-900 group-hover:bg-indigo-600 group-hover:text-white border-slate-700' : 'bg-slate-50 group-hover:bg-indigo-600 group-hover:text-white border-slate-200'}`}><ArrowRight size={18} className="text-slate-400 group-hover:text-white"/></div></div>
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          )}
                      </div>
                  </div>
              </div>
              )
          )}

          {activeView === 'history' && (
            <div className="flex-1 flex flex-col min-h-0">
              <header className={`backdrop-blur-md border-b px-6 md:px-12 py-6 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-200'}`}>
                  <div className="max-w-5xl mx-auto flex items-center gap-4">
                      <div className="bg-indigo-600 p-2.5 rounded-2xl text-white shadow-lg shadow-indigo-500/30"><History size={32}/></div>
                      <div>
                          <h2 className={`text-3xl font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Sohbet Geçmişi</h2>
                          <p className="text-slate-400 text-sm font-medium">Asistan ile yaptığın tüm görüşmelerin kronolojik kaydı.</p>
                      </div>
                  </div>
              </header>

              <div className="flex-1 overflow-y-auto p-6 md:p-12 custom-scrollbar">
                <div className="max-w-5xl mx-auto">
                  <div className="space-y-6 pb-24">
                    {historyMessages.filter(m => m.id !== 'welcome').length === 0 ? (<div className={`text-center py-24 rounded-[2.5rem] border border-dashed shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-300'}`}><div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}><History size={32} className="text-slate-500"/></div><h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Henüz geçmiş yok</h3><p className="text-slate-500 mt-2 mb-8 px-6">Gezi Asistanı ile sohbet etmeye başladığında mesajların burada listelenecek.</p><button onClick={() => setActiveView('chat')} className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-900/20 font-bold">Sohbet Başlat</button></div>) : (
                        historyMessages.filter(m => m.id !== 'welcome').map((msg) => (
                            <div key={msg.id} className={`p-8 rounded-[2rem] border shadow-sm transition-all hover:shadow-lg hover:-translate-y-1 duration-300 ${msg.role === 'user' ? (isDarkMode ? 'bg-slate-800/50 border-slate-700 ml-4 md:ml-20 relative overflow-hidden group' : 'bg-indigo-50 border-indigo-100 ml-4 md:ml-20 relative overflow-hidden group') : (isDarkMode ? 'bg-slate-800 border-slate-700 mr-4 md:mr-20' : 'bg-white border-slate-200 mr-4 md:mr-20')}`}>
                                 {msg.role === 'user' && <div className="absolute top-0 right-0 w-1 h-full bg-indigo-500"></div>}
                                <div className={`flex items-center gap-4 mb-4 pb-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}><div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md ${msg.role === 'user' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-slate-900 border border-slate-600 text-slate-400' : 'bg-slate-50 border border-slate-200 text-indigo-600')}`}>{msg.role === 'user' ? <User size={20}/> : <Cpu size={22}/>}</div><div className="flex flex-col"><span className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{msg.role === 'user' ? 'Sen' : 'Mekânname AI'}</span><span className="text-xs text-slate-500 font-medium mt-0.5">{msg.timestamp ? new Date(msg.timestamp).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : ''}</span></div></div>
                                <div className="prose prose-invert max-w-none font-medium">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={getMarkdownStyles(isDarkMode)}>
                                        {msg.text.replace('[SAVE_ROUTE_ACTION]', '')}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* 🚨 PROPLAR AŞAĞIYA EKLENDİ 🚨 */}
        <CreateTripModal isOpen={isCreateTripOpen} onClose={() => setIsCreateTripOpen(false)} onSubmit={handleCreateTripSubmit} cost={tokenCosts.create_trip} />
        <SaveRouteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={confirmSave} content={pendingContent} />
        <DeleteConfirmationModal isOpen={deleteModalState.isOpen} onClose={() => setDeleteModalState({ isOpen: false, routeId: null })} onConfirm={confirmDeleteRoute} />
        <Notification isVisible={notification.show} message={notification.message} type={notification.type} onClose={() => setNotification(prev => ({ ...prev, show: false }))} />
        <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} costs={tokenCosts} />
      </div>
    </ThemeContext.Provider>
  );
};

export default MekannameApp;